import React, { useState, useMemo } from "react";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import CommissionInvoicesTable from "../components/commission-invoices-table";
import CommissionInvoiceDrawer from "../components/commission-invoice-drawer";
import {
  useGetCommissionInvoicesQuery,
  useGetCommissionInvoiceByIdQuery,
  useGetCommissionReferralsQuery,
  useUpdateCommissionInvoiceIncludeInTotalMutation,
} from "../endpoints/commissions.endpoints";
import type { CommissionInvoice } from "../types/commissions.types";
import { formatCurrency } from "../../utils/billing-utils";
import {
  monthYearToQuarterPeriod,
  useReportCommissionViewPeriod,
} from "../commission-view-period-context";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

/** Newest-first quarters from Q1 2024 through Q4 of the current year (includes future quarters this year). */
function getQuartersForInvoices(): { label: string; year: number; quarter: number }[] {
  const currentYear = new Date().getFullYear();
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let year = currentYear; year >= 2024; year--) {
    for (let quarter = 4; quarter >= 1; quarter--) {
      quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
    }
  }
  return quarters;
}

function getYearsForInvoices(): number[] {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2].filter((year) => year >= 2024);
}

const QUARTERS = getQuartersForInvoices();
const YEARS = getYearsForInvoices();

function currentQuarterLabel(): string {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q} ${now.getFullYear()}`;
}

/** Parse paid date string to month (1–12) and year; returns null if missing/invalid */
function parsePaidDate(paidDate: string | undefined): { month: number; year: number } | null {
  if (!paidDate) return null;
  const d = new Date(paidDate);
  if (Number.isNaN(d.getTime())) return null;
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function monthToQuarter(month: number): number {
  return Math.ceil(month / 3);
}

const InvoicesView: React.FC = () => {
  const { data: invoices = [], isLoading } = useGetCommissionInvoicesQuery();
  const { data: referrals = [] } = useGetCommissionReferralsQuery();
  const [periodType, setPeriodType] = useState<"month" | "quarter">("month");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarterLabel);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<CommissionInvoice | null>(null);

  const { data: fullInvoice } = useGetCommissionInvoiceByIdQuery(selectedInvoice?.id ?? "", {
    skip: !drawerOpen || !selectedInvoice?.id,
  });
  const [updateIncludeInTotal] = useUpdateCommissionInvoiceIncludeInTotalMutation();

  const viewPeriodForLoad = useMemo(() => {
    if (periodType === "quarter") {
      const q = QUARTERS.find((x) => x.label === selectedQuarter);
      if (!q) return null;
      return { quarter: `Q${q.quarter}`, year: q.year };
    }
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    if (!month || !year) return null;
    return monthYearToQuarterPeriod(month, year);
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  useReportCommissionViewPeriod(viewPeriodForLoad);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (inv.status !== "paid") return false;
      const paid = parsePaidDate(inv.paidDate);
      if (!paid) return false;
      if (periodType === "month") {
        if (paid.month !== Number(selectedMonth) || paid.year !== Number(selectedYear)) {
          return false;
        }
      } else {
        const q = QUARTERS.find((x) => x.label === selectedQuarter);
        if (!q || paid.year !== q.year || monthToQuarter(paid.month) !== q.quarter) {
          return false;
        }
      }
      return true;
    });
  }, [invoices, periodType, selectedMonth, selectedYear, selectedQuarter]);

  const totalIncome = useMemo(() => {
    const included = filteredInvoices.filter((inv) => inv.includeInTotal !== false);
    const invoiceTotal = included.reduce((sum, inv) => sum + inv.amount, 0);
    const totalExpenses = included.reduce(
      (sum, inv) => sum + (inv.expenses ?? 0),
      0
    );
    return invoiceTotal - totalExpenses;
  }, [filteredInvoices]);

  const handleToggleIncludeInTotal = (invoice: CommissionInvoice, includeInTotal: boolean) => {
    updateIncludeInTotal({ id: invoice.id, includeInTotal });
    // Cache is updated optimistically so the invoice stays visible with toggle off/on
  };

  const handleRowClick = (invoice: CommissionInvoice) => {
    setSelectedInvoice(invoice);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <PortalDsField
            label="View by"
            htmlFor="invoices-period-type"
            filter
            className="w-[8.5rem]"
          >
            <PortalDsSelect
              id="invoices-period-type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
            >
              {PERIOD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
          {periodType === "month" ? (
            <>
              <PortalDsField
                label="Month"
                htmlFor="invoices-month"
                filter
                className="w-[9.5rem]"
              >
                <PortalDsSelect
                  id="invoices-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
              <PortalDsField
                label="Year"
                htmlFor="invoices-year"
                filter
                className="w-[7rem]"
              >
                <PortalDsSelect
                  id="invoices-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
            </>
          ) : null}
          {periodType === "quarter" && (
            <PortalDsField
              label="Quarter"
              htmlFor="invoices-quarter"
              filter
              className="w-[8.5rem]"
            >
              <PortalDsSelect
                id="invoices-quarter"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {QUARTERS.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
          )}
        </div>
        <span className={portalDs.bodySm}>
          Total Income:
          {" "}
          <span className="font-semibold text-[#0D0F1A]">
            {formatCurrency(totalIncome)}
          </span>
        </span>
      </div>
      <p className={portalDs.caption}>
        Showing only paid invoices, filtered by paid date.
      </p>
      {isLoading ? (
        <p className={`${portalDs.bodySm} py-4`}>
          Loading invoices…
        </p>
      ) : (
        <CommissionInvoicesTable
          invoices={filteredInvoices}
          onRowClick={handleRowClick}
          onToggleIncludeInTotal={handleToggleIncludeInTotal}
        />
      )}
      <CommissionInvoiceDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        invoice={fullInvoice ?? selectedInvoice}
        referrals={referrals}
        onToggleIncludeInTotal={handleToggleIncludeInTotal}
      />
    </div>
  );
};

export default InvoicesView;
